import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary';
import { Button } from '@/components/ui/button';
import { AlertCircle, Home, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { logger } from '@/utils/logger';

function ErrorFallback({ error, resetErrorBoundary }: {
  error: Error;
  resetErrorBoundary: () => void;
}) {
  const navigate = useNavigate();

  // Log error for monitoring (in production, send to error tracking service)
  useEffect(() => {
    logger.error('Application Error', { error: error.message, stack: error.stack });
  }, [error]);

  return (
    <div 
      className="flex min-h-screen items-center justify-center bg-gradient-to-b from-gray-50 to-white p-4"
      role="alert"
      aria-live="assertive"
    >
      <div className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-8 shadow-xl">
        <div className="flex justify-center">
          <div className="rounded-full bg-red-100 p-4">
            <AlertCircle className="h-8 w-8 text-red-600" aria-hidden="true" />
          </div>
        </div>
        <h2 className="mt-6 text-center text-2xl font-bold text-gray-900">
          Something went wrong
        </h2>
        <p className="mt-3 text-center text-sm text-gray-600">
          We encountered an unexpected error. Please try again or return to the homepage.
        </p>
        {process.env.NODE_ENV === 'development' && (
          <details className="mt-4 rounded-lg bg-gray-50 p-4">
            <summary className="cursor-pointer text-sm font-medium text-gray-700">
              Error Details (Development Only)
            </summary>
            <pre className="mt-2 overflow-auto text-xs text-gray-600">
              {error.message}
              {error.stack && `\n\n${error.stack}`}
            </pre>
          </details>
        )}
        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
          <Button 
            onClick={resetErrorBoundary}
            className="flex items-center justify-center gap-2"
            aria-label="Try again to reload the page"
          >
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
            Try Again
          </Button>
          <Button 
            variant="outline"
            onClick={() => navigate('/')}
            className="flex items-center justify-center gap-2"
            aria-label="Go to homepage"
          >
            <Home className="h-4 w-4" aria-hidden="true" />
            Go Home
          </Button>
        </div>
      </div>
    </div>
  );
}

export function ErrorBoundaryProvider({ children }: { children: React.ReactNode }) {
  return (
    <ReactErrorBoundary FallbackComponent={ErrorFallback}>
      {children}
    </ReactErrorBoundary>
  );
}
